import { debounce } from 'lodash';
import { useCallback, useEffect, useRef, useState } from 'react';
import type { TooltipPosition } from './types';
import { HORIZONTAL_OFFSET, VERTICAL_OFFSET } from './utils';

export const useTooltipPosition = () => {
  const triggerRef = useRef<HTMLElement>(null);
  const tooltipRef = useRef<HTMLElement>(null);
  const [position, setPosition] = useState<TooltipPosition>('bottom');

  const updatePosition = useCallback(() => {
    if (!triggerRef.current || !tooltipRef.current) return;

    const triggerRect = triggerRef.current.getBoundingClientRect();
    const tooltipRect = tooltipRef.current.getBoundingClientRect();
    const { innerWidth: viewportWidth, innerHeight: viewportHeight } = window;

    const availableSpace = {
      top: triggerRect.top,
      bottom: viewportHeight - triggerRect.bottom,
      left: triggerRect.left,
      right: viewportWidth - triggerRect.right,
    };

    const tooltipWidth = tooltipRect.width;
    const tooltipHeight = tooltipRect.height;

    const triggerWidth = triggerRect.width;

    // Extra space required to move the tooltip without it going off-screen
    const THRESHOLD = 0;

    const isOffscreenRight =
      triggerRect.left + triggerWidth + tooltipWidth + HORIZONTAL_OFFSET >
      viewportWidth - THRESHOLD;
    const isOffscreenLeft = triggerRect.left - tooltipWidth - HORIZONTAL_OFFSET < THRESHOLD;
    const isOffscreenTop = triggerRect.top - tooltipHeight - VERTICAL_OFFSET < THRESHOLD;
    const isOffscreenBottom =
      triggerRect.bottom + tooltipHeight + VERTICAL_OFFSET > viewportHeight - THRESHOLD;

    let preferredPosition: TooltipPosition = 'bottom';
    if (isOffscreenBottom) {
      if (!isOffscreenTop) {
        preferredPosition = 'top';
      } else if (!isOffscreenLeft) {
        preferredPosition = 'left';
      } else if (!isOffscreenRight) {
        preferredPosition = 'right';
      } else {
        // If it exceeds all directions, find the direction with the most space
        const { direction: directionWithMostSpace } = Object.entries(availableSpace).reduce(
          (max: { direction: TooltipPosition; space: number }, [direction, space]) =>
            space > max.space ? { direction: direction as TooltipPosition, space } : max,
          { direction: 'bottom', space: 0 },
        );
        preferredPosition = directionWithMostSpace;
      }
    }

    setPosition(preferredPosition);
  }, []);

  const debouncedUpdatePosition = debounce(updatePosition, 100);

  useEffect(() => {
    updatePosition();

    const handleResize = () => {
      debouncedUpdatePosition();
    };

    const handleScroll = () => {
      debouncedUpdatePosition();
    };

    window.addEventListener('resize', handleResize);
    window.addEventListener('scroll', handleScroll, { passive: true });

    return () => {
      window.removeEventListener('resize', handleResize);
      window.removeEventListener('scroll', handleScroll);
    };
  }, []);

  return {
    triggerRef,
    tooltipRef,
    position,
  };
};
